import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Label } from "reactstrap";
import moment from "moment-timezone";
import { omit } from "lodash";
import Checkbox from "../../../../../../components/Inputs/Checkbox";
import ColumnToggle from "../../../../../../components/NTable/Actions/ColumnToggle";
import Item from "./Content/Item";
import Content from "./Content";
import { useTable } from "../../hooks/useTable";
import { useSubTableColumns } from "../../hooks/useSubTableColumns";
import { useMultiprofileTimesheets } from "../../hooks/useMultiprofileTImesheets";
import { useDateFilters } from "../../../../hooks/useDateFilters";
import { useCustomFields } from "../../../../../Settings/CustomFields/context";
import { useGroups } from "../../../../../Settings/General/OrganisationLevelGroups/context";

function SubContent({ rowId, ids: userIds, params: baseParams, user: defaultUser, isMyOvertime }) {
    const { t } = useTranslation();
    const [checkedMap, setCheckedMap] = useState({});
    const { from, to } = useDateFilters();
    const { timesheetCustomFields } = useCustomFields();
    const { groups } = useGroups();

    const params = useMemo(() => {
        if (isMyOvertime) {
            return {
                userId: userIds,
                startDate: moment(from).format("YYYY-MM-DD"),
                endDate: moment(to).format("YYYY-MM-DD")
            }
        }
        return {
            ...(omit(baseParams, ["page", "perPage"])),
            userId: userIds
        }
    }, [isMyOvertime, baseParams, from, to, userIds]);

    const { data, isLoading, totalItems } = useMultiprofileTimesheets({ isMyOvertime, userIds, params });
    const columns = useSubTableColumns({ groupBy: false });

    const table = useTable({
        data,
        totalItems,
        loading: isLoading,
        columns,
        initialState: {
            columnVisibility: {
                "payCode_hourType": false,
                "payCode_description": false,
                job: false,
                location: false,
                overnight: false
            }
        }
    });

    const checked = !!checkedMap[rowId];
    const disabled = useMemo(() => {
        return isLoading ||
            (rowId === defaultUser.id && (
                !defaultUser?.otherProfiles ||
                defaultUser?.otherProfiles?.length === 0 ||
                totalItems === 0
            ))
    }, [totalItems, defaultUser.id, defaultUser?.otherProfiles, isLoading, rowId]);

    useEffect(() => {
        if (!timesheetCustomFields?.length) return;
        table.setColumnVisibility((prev = {}) => {
            const next = { ...prev };
            timesheetCustomFields.forEach((field) => {
                next[`customFields_${field.code}`] = false;
            });
            return next;
        });
    }, [timesheetCustomFields, table]);

    useEffect(() => {
        if (!groups?.length || groups?.length === 0) return;
        table.setColumnVisibility((prev) => {
            const columnVisibility = prev;
            groups.forEach((group) => {
                const value = `group_${group.level.replaceAll("_", "")}`;
                columnVisibility[value] = false;
            })
            return columnVisibility;
        })
    }, [groups, table]);

    useEffect(() => {
        setCheckedMap((prev) => ({
            ...prev,
            [rowId]: true
        }));
    }, [rowId]);

    return (
        <div className="bg-white rounded">
            <div className="d-flex align-items-center justify-content-between border border-top-0 rounded px-3 pb-3 pt-4">
                <div className="d-flex align-items-center">
                    <Checkbox
                        id={`timesheet-overtime-${rowId}`}
                        checked={checked}
                        disabled={disabled}
                        onChange={() =>
                            setCheckedMap((prev) => ({
                                ...prev,
                                [rowId]: !prev?.[rowId]
                            }))
                        }
                    />
                    <Label className="text-dark">
                        {checked ? t("grouped-by-profile") : t("all")}
                    </Label>
                </div>

                <ColumnToggle prefix="overtime-subtable" columns={table.getAllColumns()} />
            </div>
            {!checked ? (
                <Item table={table} />
            ) : userIds.map((id) => (
                <Content
                    userId={id}
                    data={data || []}
                    loading={isLoading}
                    groupBy={checked}
                    defaultUser={defaultUser}
                    columnVisibility={table.getState()?.columnVisibility || {}}
                />
            ))}
        </div>
    );
}

export default SubContent;
